/** POI marker images. Two kinds:
 *
 *   - uploaded logos, circle-cropped onto a white disc so they look uniform
 *     regardless of what the venue uploads;
 *   - for POIs with no logo, the category icon knocked out in white on a disc
 *     of the category colour — far more legible than a blank placeholder.
 *
 *  Since item 0371 they are drawn by the HTML marker overlay (poiMarkers.js),
 *  not registered as map images: a GL symbol cannot be lifted onto a roof, so
 *  the markers left the symbol layer. The pixels are unchanged — same 96 px
 *  canvas — and are handed out as data URLs, cached per image id so a category
 *  disc shared by twenty POIs is drawn once.
 */
import { imageUrl } from "../../api.js";
import { iconSvgMarkup, resolveIcon } from "../../lib/iconLib.js";

const SIZE = 96;

function placeholder() {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = SIZE;
  const ctx = canvas.getContext("2d");
  ctx.beginPath();
  ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 2, 0, Math.PI * 2);
  ctx.fillStyle = "#cbd5e1";
  ctx.fill();
  return canvas.toDataURL();
}

let _placeholder = null;
/** Grey disc shown until (or instead of) a POI's real image. */
export function placeholderSrc() {
  return (_placeholder ??= placeholder());
}

function loadHtmlImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

async function circular(url) {
  const img = await loadHtmlImage(url);
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = SIZE;
  const ctx = canvas.getContext("2d");

  ctx.beginPath();
  ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 1, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();

  ctx.save();
  ctx.beginPath();
  ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 4, 0, Math.PI * 2);
  ctx.clip();
  // cover-fit the source image into the disc
  const scale = Math.max(SIZE / img.width, SIZE / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, (SIZE - w) / 2, (SIZE - h) / 2, w, h);
  ctx.restore();

  ctx.beginPath();
  ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 2.5, 0, Math.PI * 2);
  ctx.lineWidth = 4;
  ctx.strokeStyle = "#ffffff";
  ctx.stroke();

  return canvas.toDataURL();
}

/** Draw the category icon in white on a filled disc. */
async function categoryDisc(iconId, color) {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = SIZE;
  const ctx = canvas.getContext("2d");

  ctx.beginPath();
  ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 4, 0, Math.PI * 2);
  ctx.fillStyle = color || "#64748b";
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = "#ffffff";
  ctx.stroke();

  const icon = await resolveIcon(iconId);
  if (icon) {
    // Data-URL SVGs do not taint the canvas, so toDataURL still works.
    const markup = iconSvgMarkup(icon, "#ffffff");
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
    const glyph = await loadHtmlImage(url);
    const box = SIZE * 0.54;
    ctx.drawImage(glyph, (SIZE - box) / 2, (SIZE - box) / 2, box, box);
  }
  return canvas.toDataURL();
}

export const logoImageId = (poi) => `logo-${poi.id}`;
export const categoryImageId = (categoryId) => `cat-${categoryId}`;

/** Which image a POI's marker uses. Category discs are shared by every
 *  logo-less POI in the category, so they are drawn once per category. */
export const poiImageId = (poi) =>
  poi.logo_path ? logoImageId(poi) : categoryImageId(poi.category_id);

// id -> Promise<dataURL|null>. Keyed by content too (logo file name, category
// icon + colour), so an edited logo or recoloured category is redrawn rather
// than served stale from the cache.
const cache = new Map();

/** Data URL of a POI's marker image, or null when it cannot be drawn (broken
 *  upload, unknown category) — the caller keeps the placeholder then. */
export function poiMarkerSrc(poi, categoriesById = {}) {
  if (poi.logo_path) {
    const key = `${logoImageId(poi)}|${poi.logo_path}`;
    if (!cache.has(key)) {
      cache.set(key, circular(imageUrl(poi.logo_path)).catch(() => null));
    }
    return cache.get(key);
  }
  if (poi.category_id == null) return Promise.resolve(null);
  const category = categoriesById[poi.category_id];
  const key = `${categoryImageId(poi.category_id)}|${category?.icon ?? ""}|${category?.color ?? ""}`;
  if (!cache.has(key)) {
    cache.set(key, categoryDisc(category?.icon, category?.color).catch(() => null));
  }
  return cache.get(key);
}
